import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Dropzone from 'react-dropzone-uploader';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import BackupIcon from '@material-ui/icons/Backup';
import { useTranslation } from 'react-i18next';
import Api from '../../../api';
import ENDPOINTS, { ENDPOINTS_SEGMENTS } from '../../../constants/endpoints';
import { projectIdSelector, tenantIdSelector } from '../../../store/selectors/formSessionSelectors';
import { isFacePhotoField } from '../../../constants/fields';
import { PHOTO_VALIDATION_HTTP_STATUS, PHOTO_VALIDATION_MODE } from '../../../constants/photoValidation';
import { usePhotoValidation } from '../../../hooks/usePhotoValidation';
import PhotoValidationModal from '../../PhotoValidationModal';
import { PhotoRequirementsTrigger } from '../../PhotoRequirementsDialog';
import { ERROR_TYPES } from '../../../constants/errorTypes';
import sanitizer from '../../../services/sanitize-service';
import FilePreview from '../../FilePreview/FilePreview';
import {
  FILE_UPLOAD_STATUSES,
  FILE_UPLOAD_DIRNAME,
  FILE_UPLOAD_MAX_SIZE_BYTES,
} from './file-upload-constants';
import { useStyles } from './useStyles';
import 'react-dropzone-uploader/dist/styles.css';
import Button from '../../Button/Button';

const InputFileUploadSingle = ({ extensiontype, description, form, field }) => {
  const { t } = useTranslation();
  const { errors, setFieldValue } = form;
  const { name, value } = field;
  const projectId = useSelector(projectIdSelector);
  const tenantId = useSelector(tenantIdSelector);
  const isFacePhoto = isFacePhotoField(name);
  const renderDropzone = !value?.length;
  const maxFiles = 1;
  const noFileValue = '';
  const noFileNameValue = '';
  const [validationError, setValidationError] = useState('');
  const classes = useStyles(validationError)();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(value || noFileValue);
  const [uploadedFileName, setUploadedFileName] = useState(noFileNameValue);
  const [localBlobUrl, setLocalBlobUrl] = useState(null);
  const photoValidation = usePhotoValidation();

  useEffect(() => {
    setUploadedFile(value);
  }, [value]);

  const getUploadParams = () => {
    const baseUrl = `${ENDPOINTS.FILE_UPLOAD_API.replace(
      ENDPOINTS_SEGMENTS.FILE_UPLOAD_DIRNAME,
      FILE_UPLOAD_DIRNAME,
    )}`;

    // For a facePhoto/Photo field type, the upload request must include the
    // isFacePhoto flag together with the current projectId and tenantId.
    let url = baseUrl;
    if (isFacePhoto) {
      const params = new URLSearchParams({ isFacePhoto: 'true' });
      if (projectId) {
        params.append('projectId', projectId);
      }
      if (tenantId) {
        params.append('tenantId', tenantId);
      }
      url = `${baseUrl}?${params.toString()}`;
    }

    return {
      url,
      method: 'post',
      headers: {},
    };
  };

  const updateValue = (fileUrl, fileName) => {
    setFieldValue(name, fileUrl);
    setUploadedFile(fileUrl);
    if (fileName) {
      setUploadedFileName(fileName);
    }
  };

  const setAcceptedFiles = (types) => {
    return types.map((typ) => `.${typ}`).join(', ');
  };

  const startUploadingProcess = () => {
    setIsUploading(true);
  };

  const endUploadingProcess = (isSuccess, message) => {
    setIsUploading(false);
    setValidationError(!isSuccess ? message : '');
  };

  // When the status is a failure,
  // the file might be still addedto the list of files in the dropzone
  // (it has a remove method)
  // in this case, we want to remove the file from the list
  const removeSelectedFileOnStatusChangeError = ({ remove } = {}) => {
    if (remove) {
      remove();
    }
  };

  const handleChangeStatus = (fileWithMeta, status) => {
    switch (status) {
      case FILE_UPLOAD_STATUSES.PREPARING: {
        startUploadingProcess();
        break;
      }
      case FILE_UPLOAD_STATUSES.REJECTED_FILE_TYPE: {
        removeSelectedFileOnStatusChangeError(fileWithMeta);
        endUploadingProcess(
          false,
          `${t('FILE_UPLOAD_REJECTED_FILE_TYPE')} ${setAcceptedFiles(extensiontype)}`,
        );
        break;
      }
      case FILE_UPLOAD_STATUSES.ERROR_FILE_SIZE: {
        removeSelectedFileOnStatusChangeError(fileWithMeta);
        endUploadingProcess(false, t('FILE_UPLOAD_ERROR_FILE_SIZE'));
        break;
      }
      case FILE_UPLOAD_STATUSES.REJECTED_MAX_FILES: {
        removeSelectedFileOnStatusChangeError(fileWithMeta);
        endUploadingProcess(false, t('FILE_UPLOAD_REJECTED_MAX_FILES'));
        break;
      }
      case FILE_UPLOAD_STATUSES.EXCEPTION_UPLOAD: {
        removeSelectedFileOnStatusChangeError(fileWithMeta);
        endUploadingProcess(false, t('AN_ERROR_OCCURED'));
        break;
      }

      case FILE_UPLOAD_STATUSES.ERROR_UPLOAD: {
        const httpStatus = fileWithMeta.xhr?.status;
        const rawResponse = fileWithMeta.xhr?.response;
        removeSelectedFileOnStatusChangeError(fileWithMeta);

        // Intercept photo validation failures (HTTP 422) for facePhoto/Photo
        // fields and show the validation dialog instead of the generic error.
        const previewUrl =
          isFacePhoto &&
          httpStatus === PHOTO_VALIDATION_HTTP_STATUS &&
          fileWithMeta.file
            ? URL.createObjectURL(fileWithMeta.file)
            : null;

        const handledAsValidation =
          isFacePhoto &&
          httpStatus === PHOTO_VALIDATION_HTTP_STATUS &&
          photoValidation.handlePotentialValidationError(
            rawResponse,
            previewUrl,
          );

        if (handledAsValidation) {
          // Stop the spinner without surfacing the generic error message.
          endUploadingProcess(true, '');
        } else {
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
          }
          endUploadingProcess(false, t('AN_ERROR_OCCURED'));
        }
        break;
      }

      case FILE_UPLOAD_STATUSES.DONE: {
        try {
          const response = JSON.parse(fileWithMeta.xhr?.response);
          const fileUrl = decodeURI(response?.location);

          const blobUrl =
            fileWithMeta.meta?.previewUrl ||
            (fileWithMeta.file ? URL.createObjectURL(fileWithMeta.file) : null);
          setLocalBlobUrl(blobUrl);
          endUploadingProcess(true, '');
          updateValue(fileUrl, fileWithMeta?.meta?.name);

          // WARN_ONLY: photo is stored; surface non-blocking warnings if present.
          // Prefer the local blob so the modal preview matches reject mode and
          // does not depend on a private S3 URL (which would 403 in <img>).
          if (isFacePhoto) {
            photoValidation.handlePotentialValidationWarnings(
              response,
              blobUrl || fileUrl,
            );
          }
        } catch (error) {
          removeSelectedFileOnStatusChangeError(fileWithMeta);
          endUploadingProcess(false, t('AN_ERROR_OCCURED'));
        }
        break;
      }
      default:
        break;
    }
  };

  const deleteFileHandler = async () => {
    if (uploadedFileName) {
      Api.delete(
        `${ENDPOINTS.FILE_UPLOAD_API.replace(
          ENDPOINTS_SEGMENTS.FILE_UPLOAD_DIRNAME,
          FILE_UPLOAD_DIRNAME,
        )}/${uploadedFileName}`,
        {},
      );
    }

    // Revoke the blob URL to free memory
    if (localBlobUrl && localBlobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(localBlobUrl);
    }
    setLocalBlobUrl(null);
    updateValue(noFileValue, noFileNameValue);
  };

  // For WARN_ONLY, "Replace Photo" clears the accepted upload so the user can
  // pick a new image. Enforce-mode rejections never stored a file.
  const handleReplacePhoto = () => {
    const shouldClearUpload =
      photoValidation.result?.validationMode === PHOTO_VALIDATION_MODE.WARN_ONLY;
    photoValidation.close();
    if (shouldClearUpload) {
      deleteFileHandler();
    }
  };

  const LayoutComponent = ({ input, previews, dropzoneProps, files }) => (
    <div className={classes.dropZoneLayout}>
      {!isUploading ? (
        <>
          <BackupIcon className={classes.dropZoneIcon} />
          <Typography className={classes.dropZoneLabel}>
            {t('FILE_UPLOAD_LABEL')}
          </Typography>
          <Button
            label={t('FILE_UPLOAD_BUTTON')}
            className={classes.dropZoneButton}
          />
        </>
      ) : (
        <>{previews}</>
      )}
      <div {...dropzoneProps}>{files?.length < maxFiles && input}</div>
    </div>
  );

  const PreviewComponent = () => (
    <>
      <CircularProgress className={classes.dropZoneIcon} />
      <Typography className={classes.dropZoneLabel}>
        {t('FILE_UPLOAD_START_UPLOAD')}
      </Typography>
    </>
  );

  useEffect(() => {
    if (errors[`${name}`] === ERROR_TYPES.FILE_UPLOAD_REQUIRED) {
      setValidationError(t('FILE_UPLOAD_REQUIRED'));
    }
  }, [errors, name, t]);

  return (
    <div className={classes.dropZoneContainer}>
      <Typography
        dangerouslySetInnerHTML={{ __html: sanitizer(description) }}
        className={classes.dropZoneDescription}
      />
      {renderDropzone && (
        <Dropzone
          getUploadParams={getUploadParams}
          onChangeStatus={handleChangeStatus}
          accept={setAcceptedFiles(extensiontype)}
          LayoutComponent={LayoutComponent}
          PreviewComponent={PreviewComponent}
          maxSizeBytes={FILE_UPLOAD_MAX_SIZE_BYTES}
          maxFiles={maxFiles}
          multiple={false}
          inputContent=""
          styles={{
            dropzone: {
              position: 'absolute',
              border: 'none',
              height: '100%',
              margin: 0,
              padding: 0,
              minHeight: '100%',
              top: 0,
              overflow: 'hidden',
              backgroundColor: 'transparent',
            },
          }}
        />
      )}

      {!isFacePhoto && (
        <>
          <Typography className={classes.dropZoneHintm8}>
            {t('ALLOWED_FILE_SIZE')}
          </Typography>
          <Typography className={classes.dropZoneHintm}>
            {`${t('ALLOWED_FILE_TYPE')} ${setAcceptedFiles(extensiontype)}`}
          </Typography>
        </>
      )}
      {isFacePhoto && <PhotoRequirementsTrigger />}
      {validationError && (
        <Typography
          dangerouslySetInnerHTML={{ __html: sanitizer(validationError) }}
          className={classes.dropZoneError}
        />
      )}
      {uploadedFile && (
        <>
          <Typography
            dangerouslySetInnerHTML={{
              __html: sanitizer(t('FILE_UPLOAD_PREVIEW')),
            }}
            className={classes.dropZoneDescription}
          />
          <FilePreview
            file={uploadedFile}
            localBlobUrl={localBlobUrl}
            deleteFile={deleteFileHandler}
          />
        </>
      )}

      <PhotoValidationModal
        open={photoValidation.isOpen}
        result={photoValidation.result}
        previewUrl={photoValidation.previewUrl}
        onReplacePhoto={handleReplacePhoto}
        onClose={photoValidation.close}
      />
    </div>
  );
};

InputFileUploadSingle.propsTypes = {
  extensiontype: PropTypes.array.isRequired,
  required: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};
export default InputFileUploadSingle;
